import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import client from "../lib/api";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Copy, Check, ArrowSquareOut, Storefront, CurrencyDollar, CalendarCheck, CalendarX, X } from "@phosphor-icons/react";
import { QRCodeCanvas } from "qrcode.react";
import ShareRow from "./ShareRow";
import { Calendar } from "./ui/calendar";
import { format } from "date-fns";
import { DEFAULT_AVAILABILITY, WEEKDAYS, HOUR_OPTIONS, onboardingLink } from "../lib/helpers";
import { toast } from "sonner";

export default function BookingPagePreview({ onGoProfile }) {
  const { user, setUser } = useAuth();
  const [copied, setCopied] = useState(false);
  const [avail, setAvail] = useState(() => user.availability || DEFAULT_AVAILABILITY);
  const [blackouts, setBlackouts] = useState(() => (user.blackout_dates || []).slice().sort());
  const [saving, setSaving] = useState(false);
  const [autoAssign, setAutoAssign] = useState(user.auto_assign_referrals !== false);
  const url = onboardingLink(user.role, user.slug);

  const toggleAutoAssign = async (v) => {
    setAutoAssign(v);
    try { const r = await client.patch("/profile", { auto_assign_referrals: v }); setUser(r.data);
      toast.success(v ? "Your referrals will auto-assign to you" : "Auto-assign off — referrals go to Open Shoots");
    } catch { setAutoAssign(!v); toast.error("Could not update preference"); }
  };

  const setDay = (d, patch) => setAvail((a) => ({ ...a, [d]: { ...(a[d] || { enabled: true, start: "07:00", end: "17:00" }), ...patch } }));
  const boDates = blackouts.map((s) => new Date(`${s}T00:00:00`));
  const onBlackoutSelect = (dates) => setBlackouts(Array.from(new Set((dates || []).map((d) => format(d, "yyyy-MM-dd")))).sort());
  const removeBO = (s) => setBlackouts((b) => b.filter((x) => x !== s));
  const saveAvailability = async () => {
    setSaving(true);
    try { const r = await client.patch("/profile", { availability: avail, blackout_dates: blackouts }); setUser(r.data); toast.success("Availability saved"); }
    catch { toast.error("Could not save availability"); }
    finally { setSaving(false); }
  };

  if (!user.slug) {
    return (
      <div className="max-w-lg border border-[#E9DDC6] rounded-xl bg-white p-10 text-center" data-testid="booking-preview-empty">
        <Storefront size={44} weight="duotone" className="mx-auto text-[#4A0000] mb-3" />
        <h2 className="font-display text-2xl">You don't have a booking page yet</h2>
        <p className="text-[#5C5A58] mt-2">Claim your link (like <span className="font-mono-num">/p/jane-doe</span>) and agents can book you directly — you earn 10% commission on every shoot booked through it.</p>
        <Button data-testid="booking-preview-setup" onClick={onGoProfile} className="mt-5 bg-[#4A0000] hover:bg-[#5C1A1A] text-white">Set up my link</Button>
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy"); document.body.removeChild(ta);
      } catch { /* ignore */ }
    }
    setCopied(true); setTimeout(() => setCopied(false), 1800);
    toast.success("Link copied");
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="booking-page-preview">
      {/* Commission note */}
      <div className="rounded-xl border border-[#B08D57]/40 bg-[#FBF3DA] p-4 flex items-start gap-3" data-testid="commission-note">
        <CurrencyDollar size={22} weight="fill" className="text-[#8a6d0a] shrink-0 mt-0.5" />
        <p className="text-sm text-[#5C4708]"><b>Earn 10% commission</b> on every shoot booked through your link — <b>even if you don't shoot it</b>. If you're available, it's auto-assigned to you (commission + your shoot fee). If not, it routes to Open Shoots for another photographer, but the 10% is still yours.</p>
      </div>

      {/* Auto-assign preference */}
      <div className="rounded-xl border border-[#E9DDC6] bg-white p-4 flex items-start gap-3" data-testid="auto-assign-pref">
        <CalendarCheck size={22} weight="duotone" className="text-[#4A0000] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <p className="text-sm font-medium text-[#1A1A1A] flex-1">Auto-assign my referrals to me</p>
            <Switch data-testid="auto-assign-toggle" checked={autoAssign} onCheckedChange={toggleAutoAssign} />
          </div>
          <p className="text-xs text-[#5C5A58] mt-1.5">
            {autoAssign
              ? <><b className="text-[#2E7D5B]">On.</b> Shoots booked through your link are automatically assigned to you when you're available. Turn this off to <b>just earn the 10% commission</b> — the shoot will go to the Open Shoots portal for other photographers to claim.</>
              : <><b className="text-[#8A6D1B]">Off.</b> Your referrals go to the Open Shoots portal for other photographers to claim. You <b>still earn your 10% commission</b> on every one. Turn it back on to shoot them yourself.</>}
          </p>
        </div>
      </div>

      {/* Shareable URL bar */}
      <div className="border border-[#E9DDC6] rounded-xl bg-white p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className="bg-white p-2 rounded-lg border border-[#E9DDC6] shrink-0 self-start">
            <QRCodeCanvas value={url} size={92} fgColor="#4A0000" bgColor="#FFFFFF" level="M" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-widest text-[#5C5A58] mb-1.5">Your booking link</p>
            <div className="flex items-center gap-2">
              <code data-testid="booking-full-url" className="flex-1 min-w-0 truncate font-mono-num text-sm text-[#4A0000] bg-[#FAF9F6] border border-[#E9DDC6] rounded-md px-3 py-2.5">{url}</code>
              <Button data-testid="booking-copy-url" onClick={copy} className="bg-[#4A0000] hover:bg-[#5C1A1A] text-white shrink-0">
                {copied ? <><Check size={16} className="mr-1.5" />Copied</> : <><Copy size={16} className="mr-1.5" />Copy link</>}
              </Button>
            </div>
            <p className="text-xs text-[#5C5A58] mt-2">Share this link (or the QR code) with agents — it's exactly what they'll book from.</p>
          </div>
        </div>

        {/* Preview button (opens the live page in a new tab) */}
        <div className="mt-5 pt-5 border-t border-[#F0ECE2]">
          <Button data-testid="booking-preview-open" asChild className="w-full h-12 bg-[#4A0000] hover:bg-[#5C1A1A] text-white text-base">
            <a href={url} target="_blank" rel="noreferrer"><ArrowSquareOut size={18} className="mr-2" weight="bold" />Preview my booking page</a>
          </Button>
          <p className="text-xs text-center text-[#5C5A58] mt-2">Opens your live page in a new tab — exactly as agents see it.</p>
        </div>

        <div className="mt-5 pt-5 border-t border-[#F0ECE2]">
          <ShareRow url={url} title={`Book a shoot with ${user.name || "me"}`} />
        </div>
      </div>

      {/* Availability — weekly hours + days off (saved together) */}
      <div className="border border-[#E9DDC6] rounded-xl bg-white p-5" data-testid="availability-editor">
        <div className="flex items-center gap-2 mb-1">
          <CalendarCheck size={18} weight="duotone" className="text-[#4A0000]" />
          <h3 className="font-display text-lg">Your availability</h3>
        </div>
        <p className="text-xs text-[#5C5A58] mb-5">Set the weekly hours you shoot, then block off any specific days. Agents always see every slot on your booking page — times outside your hours are flagged (they can still book; the shoot goes to Open Shoots and you keep your 10%), while blocked days can't be booked at all.</p>

        {/* Weekly hours */}
        <p className="text-xs uppercase tracking-widest text-[#5C5A58] mb-3">Weekly hours</p>
        <div className="space-y-1">
          {WEEKDAYS.map((name, d) => {
            const day = avail[String(d)] || { enabled: false, start: "07:00", end: "17:00" };
            return (
              <div key={d} className="flex items-center gap-3 py-1.5" data-testid={`avail-row-${d}`}>
                <Switch data-testid={`avail-toggle-${d}`} checked={!!day.enabled} onCheckedChange={(v) => setDay(String(d), { enabled: v })} />
                <span className={`w-24 text-sm ${day.enabled ? "text-[#1A1A1A]" : "text-[#9A968E]"}`}>{name}</span>
                {day.enabled ? (
                  <div className="flex items-center gap-2 text-sm">
                    <Select value={day.start} onValueChange={(v) => setDay(String(d), { start: v })}>
                      <SelectTrigger className="h-8 w-28 bg-white border-[#E9DDC6]" data-testid={`avail-start-${d}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{HOUR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <span className="text-[#5C5A58]">to</span>
                    <Select value={day.end} onValueChange={(v) => setDay(String(d), { end: v })}>
                      <SelectTrigger className="h-8 w-28 bg-white border-[#E9DDC6]" data-testid={`avail-end-${d}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{HOUR_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                ) : <span className="text-sm text-[#9A968E]">Unavailable</span>}
              </div>
            );
          })}
        </div>

        {/* Days off */}
        <div className="mt-6 pt-5 border-t border-[#F0ECE2]" data-testid="blackout-editor">
          <div className="flex items-center gap-2 mb-1">
            <CalendarX size={16} weight="duotone" className="text-[#4A0000]" />
            <p className="text-xs uppercase tracking-widest text-[#5C5A58]">Days off</p>
          </div>
          <p className="text-xs text-[#5C5A58] mb-4">Tap dates (holidays, vacation) to block them — this overrides your weekly hours.</p>
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="rounded-lg border border-[#E9DDC6] w-fit" data-testid="blackout-calendar">
              <Calendar mode="multiple" selected={boDates} onSelect={onBlackoutSelect}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs uppercase tracking-widest text-[#5C5A58] mb-2">Blocked dates ({blackouts.length})</p>
              {blackouts.length === 0 ? (
                <p className="text-sm text-[#9A968E]" data-testid="blackout-empty">No days off yet. You're bookable on every day allowed by your weekly hours.</p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="blackout-chips">
                  {blackouts.map((s) => (
                    <span key={s} data-testid={`blackout-chip-${s}`} className="inline-flex items-center gap-1.5 bg-[#4A0000]/5 text-[#4A0000] border border-[#4A0000]/20 rounded-[5px] pl-3 pr-2 py-1 text-sm">
                      {format(new Date(`${s}T00:00:00`), "MMM d, yyyy")}
                      <button type="button" data-testid={`blackout-remove-${s}`} onClick={() => removeBO(s)} className="hover:bg-[#4A0000]/10 rounded-full p-0.5"><X size={12} weight="bold" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <Button data-testid="avail-save" onClick={saveAvailability} disabled={saving} className="mt-6 bg-[#4A0000] hover:bg-[#5C1A1A] text-white">{saving ? "Saving…" : "Save availability"}</Button>
      </div>
    </div>
  );
}
